/**
 * Payback Filter — show the ROI math behind every recommendation.
 * Lives at /admin/payback-filter.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import SEOHead from '../../components/SEOHead';
import {
  ArrowLeft, Calculator, Loader2, CheckCircle2, XCircle, Plus, Trash2,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  const a = Math.abs(Number(n) || 0);
  if (a >= 1_000) return `$${(a / 1_000).toFixed(1)}K`;
  return `$${a.toFixed(0)}`;
};

const DEFAULTS = [
  { id: 'rec-1', title: 'Right-size m5.xlarge → m5.large', monthly_savings_usd: 95, estimated_hours_to_implement: 4, source_engine: 'rightsizer' },
  { id: 'rec-2', title: 'Delete unattached EBS volume', monthly_savings_usd: 18, estimated_hours_to_implement: 0.5, source_engine: 'orphan-finder' },
  { id: 'rec-3', title: 'Decommission idle staging cluster', monthly_savings_usd: 320, estimated_hours_to_implement: 2, source_engine: 'zombie-hunter' },
  { id: 'rec-4', title: 'Switch to Graviton instance family', monthly_savings_usd: 75, estimated_hours_to_implement: 16, source_engine: 'arm-migration' },
];

const PaybackFilter = () => {
  const [recs, setRecs] = useState(DEFAULTS);
  const [hourlyRate, setHourlyRate] = useState(150);
  const [minRoi, setMinRoi] = useState(3.0);
  const [thresholds, setThresholds] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/payback-filter/thresholds`);
        setThresholds(data);
      } catch {}
    })();
  }, []);

  const updateRec = (idx, key, val) => {
    setRecs(recs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };

  const addRec = () => {
    setRecs([...recs, {
      id: `rec-${recs.length + 1}`, title: 'New recommendation',
      monthly_savings_usd: 100, estimated_hours_to_implement: 2,
      source_engine: 'manual',
    }]);
  };

  const removeRec = (idx) => setRecs(recs.filter((_, i) => i !== idx));

  const score = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/payback-filter/score`, {
        recommendations: recs,
        engineer_hourly_cost_usd: hourlyRate,
        min_roi_ratio: minRoi,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResults(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEOHead title="Payback Filter · CARTIEAI" noindex />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-6">
          <Link to="/admin/zombie-hunter" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="pf-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Zombie Hunter
          </Link>
        </div>

        <div className="mb-8">
          <Badge className="bg-amber-500/20 text-amber-200 mb-3">Admin · ROI math</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="pf-title">
            <Calculator className="w-7 h-7 inline-block mr-2 text-amber-400" />
            Payback filter
          </h1>
          <p className="text-sm sm:text-base text-slate-700 max-w-3xl">
            A $100/mo savings that takes 4 engineer hours is a <b>net loss</b> in month-1 (4 × $150 = $600 vs $100 saved).
            Most FinOps tools hide this math. We surface it — and refuse to recommend anything below a 3× first-year ROI.
          </p>
          {thresholds && (
            <div className="mt-3 text-xs text-slate-600 italic" data-testid="pf-rationale">
              {thresholds.rationale}
            </div>
          )}
        </div>

        <Card className="border-emerald-500/20 mb-4">
          <CardHeader>
            <CardTitle className="text-lg">Recommendations to evaluate</CardTitle>
            <CardDescription className="text-slate-400">Edit values inline. Run "Score" to see which pass the 3× rule.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Engineer hourly cost ($)</label>
                <Input type="number" value={hourlyRate} onChange={(e) => setHourlyRate(Number(e.target.value) || 150)} data-testid="pf-hourly-rate" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700 mb-1 block">Min first-year ROI ratio (×)</label>
                <Input type="number" step="0.1" value={minRoi} onChange={(e) => setMinRoi(Number(e.target.value) || 3.0)} data-testid="pf-min-roi" />
              </div>
            </div>

            <div className="space-y-2" data-testid="pf-recs-list">
              {recs.map((r, i) => (
                <div key={i} className="flex items-center gap-2 border border-emerald-500/20 rounded-md p-2 bg-slate-900 text-slate-100" data-testid={`pf-rec-${i}`}>
                  <Input value={r.title} onChange={(e) => updateRec(i, 'title', e.target.value)} placeholder="Title" className="flex-1 h-8 text-xs" />
                  <Input type="number" value={r.monthly_savings_usd} onChange={(e) => updateRec(i, 'monthly_savings_usd', Number(e.target.value) || 0)} className="w-24 h-8 text-xs text-right" placeholder="$/mo" />
                  <Input type="number" step="0.5" value={r.estimated_hours_to_implement} onChange={(e) => updateRec(i, 'estimated_hours_to_implement', Number(e.target.value) || 0)} className="w-20 h-8 text-xs text-right" placeholder="hrs" />
                  <Button onClick={() => removeRec(i)} variant="outline" size="sm" className="h-8 px-2 text-rose-300 border-rose-500/40" data-testid={`pf-rec-remove-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex gap-2 mt-3">
              <Button onClick={addRec} variant="outline" size="sm" data-testid="pf-add-rec"><Plus className="w-3 h-3 mr-1" /> Add row</Button>
              <Button onClick={score} disabled={busy || recs.length === 0} className="bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/20" data-testid="pf-score-btn">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Scoring…</> : <><Calculator className="w-4 h-4 mr-1.5" /> Run payback filter</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {results && (
          <>
            <Card className="border-amber-500/30 mb-4">
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div><div className="text-xs text-slate-600">Input</div><div className="text-xl font-bold tabular-nums">{results.summary.input_count}</div></div>
                  <div><div className="text-xs text-slate-600">Approved</div><div className="text-xl font-bold text-emerald-300 tabular-nums" data-testid="pf-approved-count">{results.summary.approved_count}</div></div>
                  <div><div className="text-xs text-slate-600">Rejected</div><div className="text-xl font-bold text-rose-300 tabular-nums" data-testid="pf-rejected-count">{results.summary.rejected_count}</div></div>
                  <div><div className="text-xs text-slate-600">Approved annual</div><div className="text-xl font-bold text-emerald-300 tabular-nums">{fmtMoney(results.summary.approved_annual_usd)}</div></div>
                </div>
                <div className="mt-2 text-[11px] text-slate-600">
                  {results.summary.rejected_pct}% of incoming recommendations failed the 3× rule and would have wasted engineer time.
                </div>
              </CardContent>
            </Card>

            {results.approved.length > 0 && (
              <Card className="border-emerald-500/30 mb-4">
                <CardHeader className="pb-2"><CardTitle className="text-base text-emerald-100"><CheckCircle2 className="w-4 h-4 inline mr-1" /> Approved</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="pf-approved-list">
                    {results.approved.map((r, i) => (
                      <div key={r.id} className="flex items-center justify-between text-sm border-b border-emerald-500/20 pb-2 last:border-0" data-testid={`pf-approved-${i}`}>
                        <div>
                          <div className="text-white">{r.title}</div>
                          <div className="text-xs text-slate-600">Payback {r.payback_months} mo · ROI {r.roi_first_year}× first year</div>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-200">{fmtMoney(r.annual_savings_usd)}/yr</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {results.rejected.length > 0 && (
              <Card className="border-rose-500/30">
                <CardHeader className="pb-2"><CardTitle className="text-base text-rose-200"><XCircle className="w-4 h-4 inline mr-1" /> Rejected</CardTitle><CardDescription className="text-slate-400">These would cost more in engineer time than they save.</CardDescription></CardHeader>
                <CardContent>
                  <div className="space-y-2" data-testid="pf-rejected-list">
                    {results.rejected.map((r, i) => (
                      <div key={r.id} className="flex items-center justify-between text-sm border-b border-rose-500/20 pb-2 last:border-0 opacity-70" data-testid={`pf-rejected-${i}`}>
                        <div>
                          <div className="text-slate-900 line-through">{r.title}</div>
                          <div className="text-xs text-rose-300">Payback {r.payback_months} mo · ROI {r.roi_first_year}× (under {results.thresholds.min_roi_ratio}× threshold)</div>
                        </div>
                        <Badge className="bg-rose-500/20 text-rose-200">{fmtMoney(r.annual_savings_usd)}/yr · skip</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaybackFilter;
